import { memo, useState } from 'react'

import { ContactStatus } from 'clickNgo/entities/contact'
import { fetchContactsByPartnerId } from 'clickNgo/entities/contact/api'
import {
  fetchContactFlagsByIds,
  mapIContactFlagToGroupedContactFlags,
} from 'clickNgo/entities/contactFlag'
import {
  getContactFlagsToReassign,
  getContactRolesToReassign,
} from 'clickNgo/features/contact/contactReassignment/lib'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { NotificationController } from 'core-components/library/popup'
import Logger from 'core/utils/Logger'

import { ContactDeactivationError, ContactDeactivationStep } from '../../../configs'
import { useContactData, useReassignment } from '../../../contexts'
import { ContactDeactivationStepProps } from '../../../models'
import { contactDeactivationModalTranslations } from '../../../translation'
import { ContactDeactivationFooter } from '../../contactDeactivationFooter'
import { ContactDeactivationHeader } from '../../contactDeactivationHeader'

import styles from './WelcomeStep.module.scss'

export const WelcomeStep = memo<ContactDeactivationStepProps>(({ onProceed, onCancel }) => {
  const { contact, contactFlags } = useContactData()
  const { setAvailableContacts, setRolesToReassign, setFlagsToReassign } = useReassignment()
  const [loaderState, setLoaderState] = useState({ isLoading: false, message: '' })

  const handleProceedClick = async () => {
    try {
      setLoaderState({ isLoading: true, message: 'Loading contacts and validating...' })
      const availableContacts = (await fetchContactsByPartnerId(contact.partnerId)).filter(
        (c) =>
          c.contactId !== contact.contactId &&
          !c.contactId.startsWith('new-') &&
          c.status !== ContactStatus.Inactive
      )

      const availableContactFlags = await fetchContactFlagsByIds(
        availableContacts.map((el) => el.contactId),
        contact.topPartnerId
      )

      const importantFlags = getContactFlagsToReassign(contactFlags)
      const availableContactsImportantFlags = getContactFlagsToReassign(
        mapIContactFlagToGroupedContactFlags(availableContactFlags)
      )

      const flagsToReassign = importantFlags.filter(
        (f) => !availableContactsImportantFlags.some((a) => a.flag.code === f.flag.code)
      )

      const isNeedReassignFlag = flagsToReassign.length > 0

      const rolesToReassign = getContactRolesToReassign(contact.hpRoles)

      setAvailableContacts(availableContacts)
      setRolesToReassign(rolesToReassign)
      if (isNeedReassignFlag) {
        setFlagsToReassign(flagsToReassign)
      }

      if (availableContacts.length === 0) {
        Logger.info('No available contacts found for reassignment.')
        onProceed(ContactDeactivationStep.Backup)
      } else if (rolesToReassign.length > 0) {
        Logger.info('Roles to reassign found:', rolesToReassign)
        onProceed(ContactDeactivationStep.ReassignRoles)
      } else if (isNeedReassignFlag) {
        Logger.info('Flags to reassign found:', flagsToReassign)
        onProceed(ContactDeactivationStep.ReassignFlags)
      } else {
        Logger.info('No roles or flags to reassign found.')
        onProceed(ContactDeactivationStep.Completion)
      }
    } catch (error) {
      Logger.error(ContactDeactivationError.FetchContacts, error)
      NotificationController.showError({ message: ContactDeactivationError.FetchContacts })
    } finally {
      setLoaderState({ isLoading: false, message: '' })
    }
  }
  return (
    <div className={styles.container}>
      <LoaderOverlay isVisible={loaderState.isLoading} message={loaderState.message} />
      <ContactDeactivationHeader
        title={contactDeactivationModalTranslations.welcomeStep.title}
        description={contactDeactivationModalTranslations.welcomeStep.description}
        descriptionParams={[{ key: '{email}', value: contact.email }]}
      />
      <ContactDeactivationFooter onCancel={onCancel} onProceed={handleProceedClick} />
    </div>
  )
})

WelcomeStep.displayName = 'WelcomeStep'
